import { useApolloClient } from '@apollo/client';
import { CloudDownload } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  CircularProgress,
  Divider,
  Grid,
  Icon,
  Link,
  Paper,
  Typography
} from '@mui/material';
import { Box } from '@mui/system';

import { useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';

import USER_QUERY from 'gql/queries/currentUser.gql';

import { useModulesBySelectionForCoursesQuery } from 'api/hooks/modules';
import { EntryWithHeadline } from 'app/components';
import { DateBox } from 'app/components';
import { RichList } from 'app/components/RichList';
import { NoItems } from 'app/components/RichList/components/NoItems';
import { ModuleItem } from 'app/components/RichList/items/AWPM-FWPM/ModuleItem';
import { handleDownload } from 'app/components/Util/downloadAttachement';
import { WatchlistAddButton } from 'app/components/WatchlistAddButton';
import {
  SelectionState,
  SubjectType,
  Ordered
} from 'app/containers/AWPM-FWPM/ModuleSelections/models';
import { getCurrentSelectionState, studentSemesterParser } from 'app/containers/AWPM-FWPM/helper';
import { translations } from 'locales/i18n';

import { ModuleStudentSelection } from '../../ModuleSelection';
import { StudentModuleSelection } from './studentModuleSelection';

export const PreviewTab = ({
  moduleSelection,
  subjectType
}: {
  moduleSelection: ModuleStudentSelection;
  subjectType: SubjectType;
}) => {
  const [expanded, setExpanded] = useState<string | false>(false);
  const { t } = useTranslation();
  const client = useApolloClient();
  const { currentUser } = client.readQuery({ query: USER_QUERY });

  const { data: modules, isLoading } = useModulesBySelectionForCoursesQuery({
    params: {
      selectionId: moduleSelection.id,
      courses: currentUser.courses.map(course => course.id),
      semester: currentUser.relationships.map(relation =>
        studentSemesterParser(relation.degree, relation.semester)
      ),
      nameSort: Ordered.Ascending,
      groupingSort: Ordered.Ascending
    }
  });

  const handleChange = (panel: string) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Grid component={Paper} elevation={2} item>
          <Box className="previewTab">
            <Box className="previewTab__heading-box">
              <Box
                className="previewTab__heading-box__information"
                style={{ backgroundColor: '#ffffff' }}
              >
                <Box justifyContent="space-between" flexDirection="row" display="flex">
                  <Typography component="h2">
                    {t(translations.pages.moduleSelectionsDetail.modulePreview.information.title())}
                  </Typography>
                  {!isMobile && (
                    <WatchlistAddButton
                      categoryName="MODULE_SELECTION"
                      description={moduleSelection.generalNotes || ''}
                      title={moduleSelection.title}
                      date={moduleSelection.startDate}
                    />
                  )}
                </Box>
                <Divider />
                <Box m={2}>
                  <DateBox
                    endDate={moduleSelection.endDate}
                    startDate={moduleSelection.startDate}
                  />
                  <Grid container justifyContent={'space-between'}>
                    <Grid
                      item
                      className="previewTab__heading-box__information__headline-entry"
                      xs={12}
                      md={4}
                    >
                      <EntryWithHeadline
                        entry={`${t(
                          translations.pages.moduleSelectionsDetail.modulePreview.information.until()
                        )} ${
                          moduleSelection.dischargeByStudentsUntil
                            ? new Date(moduleSelection.dischargeByStudentsUntil).toLocaleString(
                                'de-DE',
                                {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                }
                              )
                            : '-/-'
                        }`}
                        headline={t(
                          translations.pages.moduleSelectionsDetail.modulePreview.information.dischargeByStudentsUntil()
                        )}
                      />
                    </Grid>
                    <Grid
                      item
                      className="previewTab__heading-box__information__headline-entry"
                      xs={12}
                      md={4}
                    >
                      <EntryWithHeadline
                        entry={
                          moduleSelection.contentVisibleFrom
                            ? new Date(moduleSelection.contentVisibleFrom).toLocaleString('de-DE', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                            : '-/-'
                        }
                        headline={t(
                          translations.pages.moduleSelectionsDetail.modulePreview.information.contentVisibleFrom()
                        )}
                      />
                    </Grid>
                    <Grid
                      item
                      className="previewTab__heading-box__information__headline-entry"
                      xs={12}
                      md={4}
                    >
                      <EntryWithHeadline
                        entry={
                          moduleSelection.publishedAt
                            ? new Date(moduleSelection.publishedAt).toLocaleDateString('de-DE', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: 'numeric',
                                minute: 'numeric'
                              })
                            : '-/-'
                        }
                        headline={t(
                          translations.pages.moduleSelectionsDetail.modulePreview.information.publishedAt()
                        )}
                      />
                    </Grid>
                  </Grid>
                  <Accordion expanded={expanded === 'panel1'} onChange={handleChange('panel1')}>
                    <AccordionSummary
                      expandIcon={<Icon className="fas fa-caret-down" />}
                      aria-controls="panel1bh-content"
                      id="panel1bh-header"
                    >
                      <Typography>
                        {t(
                          translations.pages.moduleSelectionsDetail.modulePreview.information.generalInfo()
                        )}
                      </Typography>
                    </AccordionSummary>
                    <Divider />
                    <AccordionDetails>
                      <ReactQuill
                        readOnly={true}
                        theme="bubble"
                        value={moduleSelection.generalNotes}
                      />
                    </AccordionDetails>
                  </Accordion>
                  {!!moduleSelection.electionTip && (
                    <Accordion expanded={expanded === 'panel2'} onChange={handleChange('panel2')}>
                      <AccordionSummary
                        expandIcon={<Icon className="fas fa-caret-down" />}
                        aria-controls="panel2bh-content"
                        id="panel2bh-header"
                      >
                        <Typography>
                          {t(
                            translations.pages.moduleSelectionsDetail.modulePreview.information.electionTips()
                          )}
                        </Typography>
                      </AccordionSummary>
                      <Divider />
                      <AccordionDetails>
                        <ReactQuill
                          readOnly={true}
                          theme="bubble"
                          value={moduleSelection.electionTip}
                        />
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {!isMobile &&
                    moduleSelection?.attachments?.map(attachment => (
                      <Link
                        onClick={() =>
                          handleDownload(
                            attachment.originalname,
                            `/module-selections/${moduleSelection.id}/attachments/${attachment.id}/download`
                          )
                        }
                        key={attachment.name}
                        style={{ cursor: 'pointer' }}
                      >
                        <CloudDownload /> {attachment.originalname}
                      </Link>
                    ))}
                </Box>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Grid component={Paper} elevation={2} item style={{ paddingBottom: '1em' }}>
          <Box>
            <Divider />
            <Box className="previewTab__courses">
              <Typography component="h2">
                {t(translations.pages.studentView.selectableModules())}
              </Typography>
              <RichList>
                <Grid container justifyContent="flex-start" item xs={12} spacing={2}>
                  {isLoading ? (
                    <CircularProgress />
                  ) : modules?.length ? (
                    modules.map(module => {
                      return (
                        <Grid
                          xs={12}
                          md={6}
                          lg={4}
                          item
                          alignContent="center"
                          justifyContent="center"
                          key={module.id}
                        >
                          <ModuleItem
                            module={module}
                            subjectType={subjectType}
                            deactivateButtons={true}
                            inStudentView={true}
                            /*checked={
                              values.modulePreferences.filter(cm => cm.id === module.id).length > 0
                            }*/
                            disabled={!module.isTakingPlace}
                          />
                        </Grid>
                      );
                    })
                  ) : (
                    <Box
                      justifyContent="center"
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      p={2}
                      flexGrow={1}
                      className="previewTab__courses__no-items"
                    >
                      <NoItems
                        message={t(
                          translations.pages.moduleSelectionsDetail.modulePreview.noItems()
                        )}
                      />
                    </Box>
                  )}
                </Grid>
              </RichList>
            </Box>
          </Box>
        </Grid>
      </Grid>
      {getCurrentSelectionState(moduleSelection) === SelectionState.Active && (
        <Grid item xs={12}>
          <Grid component={Paper} elevation={2} item>
            <Box className="previewTab__courses">
              {isLoading ? (
                <CircularProgress
                  sx={{
                    display: 'flex',
                    justifySelf: 'center'
                  }}
                />
              ) : (
                <StudentModuleSelection
                  maxSelectedModules={moduleSelection.maxSelectedModules}
                  selectableModules={modules || []}
                  lengthOfTheWishlist={moduleSelection.lengthOfTheWishlist}
                  subjectType={subjectType}
                  moduleSelectionId={moduleSelection.id}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};
